import type { DayTimeParting } from '@/entities/campaign';
import type {
  BuildCampaignScreenEditPayloadParams,
  CampaignScreenEditPayload,
  CampaignScreenEditScreen,
  CampaignScreenEditSlot,
} from '../types';
import { buildSlotDayTimeParting } from './schedulingTransformer';
import {
  buildCreativeLookup,
  transformScreensForEdit,
} from './campaignTransforms';
import { buildFinalCampaignPayload } from './payloadBuilder';

const cloneDayTimeParting = (dayTimeParting?: DayTimeParting): DayTimeParting =>
  Object.entries(dayTimeParting ?? {}).reduce<DayTimeParting>(
    (result, [day, ranges]) => ({
      ...result,
      [day]: ranges.map(range => ({ ...range })),
    }),
    {}
  );

const resolveCampaignBudget = (
  campaign: BuildCampaignScreenEditPayloadParams['campaign']
): number => campaign.campaignBudget ?? campaign.costPerUnit?.microAmount ?? 0;

const resolveBrandFolder = (
  campaign: BuildCampaignScreenEditPayloadParams['campaign']
) => {
  const selectedBrandId = campaign.selectedBrand?.id;

  if (selectedBrandId) {
    return {
      id: selectedBrandId,
      name: campaign.selectedBrand?.name,
    };
  }

  if (campaign.brandCreative) {
    return { id: campaign.brandCreative };
  }

  return null;
};

const normalizeCampaignDate = (rawDate?: string): string => {
  if (!rawDate) return '';

  const trimmedDate = rawDate.trim();
  const [datePart] = trimmedDate.split(' ');
  const datePieces = datePart.split('-');

  if (datePieces[0]?.length === 4) {
    return datePart;
  }

  if (datePieces.length === 3) {
    const [day, month, year] = datePieces;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  return trimmedDate;
};

const findScreenIndex = (
  screens: CampaignScreenEditScreen[],
  screenId: string
) => screens.findIndex(screen => screen.id === screenId);

const prepareTargetSlots = (
  slots: CampaignScreenEditSlot[],
  dayTimePartingOverride?: DayTimeParting
): CampaignScreenEditSlot[] =>
  slots.map((slot, index) => ({
    ...slot,
    index: slot.index ?? index,
    originalDayTimeParting: cloneDayTimeParting(
      slot.originalDayTimeParting ?? slot.dayTimeParting
    ),
    dayTimeParting: cloneDayTimeParting(
      dayTimePartingOverride ?? slot.dayTimeParting
    ),
  }));

export const buildCampaignScreenEditPayload = ({
  campaign,
  screenId,
  partnerId,
  editedSlots,
  screenScheduleChange,
  useFallback,
}: BuildCampaignScreenEditPayloadParams): CampaignScreenEditPayload => {
  if (!partnerId) {
    throw new Error('Partner ID is required for campaign screen edit');
  }

  if (!campaign.screens.length) {
    throw new Error('Campaign must include screens for campaign screen edit');
  }

  const creativeLookup = buildCreativeLookup(campaign.creatives ?? []);
  const selectedScreens = transformScreensForEdit(
    campaign.screens,
    creativeLookup
  ) as CampaignScreenEditScreen[];
  const targetScreenIndex = findScreenIndex(selectedScreens, screenId);

  if (targetScreenIndex === -1) {
    throw new Error('Selected screen was not found in this campaign');
  }

  const targetScreen = selectedScreens[targetScreenIndex];
  const dayTimePartingOverride = screenScheduleChange
    ? buildSlotDayTimeParting(screenScheduleChange)
    : undefined;
  const targetSlots = prepareTargetSlots(
    editedSlots ?? targetScreen.slots,
    dayTimePartingOverride
  );

  selectedScreens[targetScreenIndex] = {
    ...targetScreen,
    slots: targetSlots,
  };

  const payload = buildFinalCampaignPayload(
    {
      name: campaign.name,
      campaignStartDate: normalizeCampaignDate(
        campaign.campaignStartDate ?? campaign.startDate
      ),
      campaignEndDate: normalizeCampaignDate(
        campaign.campaignEndDate ?? campaign.endDate
      ),
      campaignBudget: resolveCampaignBudget(campaign),
      source: campaign.source || 'SCREEN_OWNER',
      brandFolder: resolveBrandFolder(campaign),
      industry: campaign.industry,
      agency: campaign.agency,
      useFallback,
    },
    selectedScreens,
    partnerId
  ) as CampaignScreenEditPayload;

  return payload;
};
